// src/pages/NhapDiem/NhapDiem.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { getHocSinhByLop, getDiemSoHocSinh, addDiemSo } from "../../services/hocSinhService";
import { getAllLopHoc } from "../../services/nguoiDungService";
import { useToast } from "../../components/Toast/ToastContext";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import UnsavedModal from "../../components/UnsavedModal/UnsavedModal";
import { getNamHocActive, getActiveHocKy } from "../../services/namHocService";
import { ROLES } from "../../constants";
import NhapDiemMobile from "./NhapDiemMobile";
import NhapDiemDesktop from "./NhapDiemDesktop";

const LIMIT = { mieng: 3, '15phut': 1, '1tiet': 1, hocKy: 1 };
const CACHE_KEY = 'nhapdiem_cache';

export const LOAI_DIEM_TABS = [
  { key: 'mieng', label: 'Miệng', icon: 'record_voice_over', color: 'text-primary', bg: 'bg-primary-container' },
  { key: '15phut', label: '15 phút', icon: 'timer', color: 'text-secondary', bg: 'bg-secondary-container' },
  { key: '1tiet', label: '1 tiết', icon: 'assignment', color: 'text-tertiary', bg: 'bg-tertiary-container' },
  { key: 'hocKy', label: 'Học kỳ', icon: 'school', color: 'text-error', bg: 'bg-error-container' },
];

const SORT_OPTIONS = [
  { value: 'ten-asc', label: 'Tên A→Z' },
  { value: 'ten-desc', label: 'Tên Z→A' },
  { value: 'tb-desc', label: 'TB cao→thấp' },
  { value: 'tb-asc', label: 'TB thấp→cao' },
];

export const getDiemColor = (diem) => {
  if (diem >= 8) return 'text-green-600';
  if (diem >= 6.5) return 'text-yellow-600';
  if (diem < 5) return 'text-error';
  return 'text-on-surface';
};

const loadCache = () => { try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || {}; } catch (e) { return {}; } };
const saveCache = (cache) => { try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) {} };

const NhapDiem = () => {
  const { user, hasRole } = useAuthContext();
  const { success, error } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = hasRole(ROLES.ADMIN);
  const isGVCN = hasRole(ROLES.GVCN);
  const canEdit = isGVCN;

  // ===== ĐỌC SEARCH TỪ URL =====
  const searchParams = new URLSearchParams(location.search);
  const searchFromProfile = searchParams.get('search') || '';

  const cacheRef = React.useRef(loadCache());
  const [lopHocList, setLopHocList] = useState([]);
  const [selectedLop, setSelectedLop] = useState(user?.maLopCN || "");
  const [hocSinh, setHocSinh] = useState([]);
  const [hocSinhGoc, setHocSinhGoc] = useState([]);
  const [diemData, setDiemData] = useState({});
  const [diemGoc, setDiemGoc] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [changedItems, setChangedItems] = useState(new Set());
  const [sortBy, setSortBy] = useState('ten-asc');
  const [filterText, setFilterText] = useState(searchFromProfile); // ← SỬA
  const [activeTab, setActiveTab] = useState('mieng');
  const [hocKy, setHocKy] = useState('HK1');
  const [namHoc, setNamHoc] = useState('2025-2026');

  const { showConfirm, confirmLeave, confirmStay } = useUnsavedChanges(hasChanges);

  useEffect(() => {
    const init = async () => {
      try { const nh = await getNamHocActive(); if (nh) setNamHoc(nh.tenNamHoc || nh.id || '2025-2026'); } catch(e) {}
      try { const hk = await getActiveHocKy(); if (hk) setHocKy(hk.hocKy); } catch(e) {}
    };
    init();
  }, []);

  useEffect(() => { getAllLopHoc().then(setLopHocList); }, []);

  const loadData = useCallback(async (forceReload = false) => {
    if (!selectedLop) return;
    const cacheKey = `${selectedLop}_${namHoc}_${hocKy}`;
    const cached = cacheRef.current[cacheKey];
    if (!forceReload && cached && cached.timestamp > Date.now() - 30 * 60 * 1000) {
      setHocSinhGoc(cached.hsList);
      setDiemData(JSON.parse(JSON.stringify(cached.diemMap)));
      setDiemGoc(JSON.parse(JSON.stringify(cached.diemMap)));
      setHasChanges(false); setChangedItems(new Set());
      return;
    }
    setLoading(true);
    try {
      let hsList = await getHocSinhByLop(selectedLop);
      hsList.sort((a, b) => (a.hoTen || "").split(" ").pop().toLowerCase().localeCompare((b.hoTen || "").split(" ").pop().toLowerCase()));
      const diemPromises = hsList.map(hs => getDiemSoHocSinh(hs.id, namHoc));
      const allDiem = await Promise.all(diemPromises);
      const diemMap = {};
      hsList.forEach((hs, i) => {
        diemMap[hs.id] = { mieng: [], '15phut': [], '1tiet': [], hocKy: [] };
        (allDiem[i] || []).forEach(d => {
          if (d.hocKy === hocKy && diemMap[hs.id][d.loaiDiem] && diemMap[hs.id][d.loaiDiem].length < LIMIT[d.loaiDiem]) {
            diemMap[hs.id][d.loaiDiem].push(d.diem);
          }
        });
      });
      cacheRef.current[cacheKey] = { hsList, diemMap: JSON.parse(JSON.stringify(diemMap)), timestamp: Date.now() };
      saveCache(cacheRef.current);
      setHocSinhGoc(hsList); setDiemData(JSON.parse(JSON.stringify(diemMap))); setDiemGoc(JSON.parse(JSON.stringify(diemMap)));
      setHasChanges(false); setChangedItems(new Set());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedLop, namHoc, hocKy]);

  useEffect(() => { loadData(); }, [loadData]);

  const getDiemTB = (hsId) => {
    const d = diemData[hsId]; if (!d) return '—';
    const m = d.mieng.filter(v => v > 0), m15 = d['15phut'].filter(v => v > 0), m1 = d['1tiet'].filter(v => v > 0), hk = d.hocKy.filter(v => v > 0);
    if (!m.length || !m15.length || !m1.length || !hk.length) return '—';
    return ((m.reduce((a,b)=>a+b,0)/m.length + m15[0] + m1[0]*2 + hk[0]*3)/7).toFixed(1);
  };

  useEffect(() => {
    let result = [...hocSinhGoc];
    if (filterText.trim()) { const t = filterText.toLowerCase(); result = result.filter(hs => (hs.hoTen || '').toLowerCase().includes(t) || (hs.tenThanh || '').toLowerCase().includes(t)); }
    switch (sortBy) {
      case 'ten-asc': result.sort((a, b) => (a.hoTen || "").split(" ").pop().toLowerCase().localeCompare((b.hoTen || "").split(" ").pop().toLowerCase())); break;
      case 'ten-desc': result.sort((a, b) => (b.hoTen || "").split(" ").pop().toLowerCase().localeCompare((a.hoTen || "").split(" ").pop().toLowerCase())); break;
      case 'tb-desc': result.sort((a, b) => parseFloat(getDiemTB(b.id)) - parseFloat(getDiemTB(a.id))); break;
      case 'tb-asc': result.sort((a, b) => parseFloat(getDiemTB(a.id)) - parseFloat(getDiemTB(b.id))); break;
    }
    setHocSinh(result);
  }, [hocSinhGoc, sortBy, filterText, diemData]);

  const updateDiem = (hsId, loaiDiem, index, value) => {
    if (value !== '' && (isNaN(value) || value < 0 || value > 10)) return;
    setHasChanges(true); setChangedItems(prev => new Set([...prev, hsId]));
    setDiemData(prev => {
      const nd = JSON.parse(JSON.stringify(prev));
      if (!nd[hsId]) nd[hsId] = { mieng: [], '15phut': [], '1tiet': [], hocKy: [] };
      const arr = nd[hsId][loaiDiem];
      if (value === '') arr.splice(index, 1);
      else { const nv = parseFloat(value); if (index >= arr.length) arr.push(nv); else arr[index] = nv; }
      return nd;
    });
  };

  const handleSave = async () => {
    if (changedItems.size === 0) { error("Không có thay đổi!"); return; }
    setSaving(true);
    try {
      const { deleteDoc, doc } = await import("firebase/firestore");
      const { db } = await import("../../config/firebase");
      for (const hsId of changedItems) {
        const d = diemData[hsId]; if (!d) continue;
        const old = await getDiemSoHocSinh(hsId, namHoc);
        for (const od of old) { if (od.hocKy === hocKy) await deleteDoc(doc(db, "hocSinh", hsId, "diemSo", od.id)); }
        for (const loai of ['mieng', '15phut', '1tiet', 'hocKy']) for (const diem of (d[loai] || [])) if (diem > 0) await addDiemSo(hsId, { loaiDiem: loai, diem, hocKy, namHoc, nguoiNhapId: user.uid });
      }
      const cacheKey = `${selectedLop}_${namHoc}_${hocKy}`;
      cacheRef.current[cacheKey] = { hsList: hocSinhGoc, diemMap: JSON.parse(JSON.stringify(diemData)), timestamp: Date.now() };
      saveCache(cacheRef.current);
      setDiemGoc(JSON.parse(JSON.stringify(diemData)));
      setHasChanges(false); setChangedItems(new Set());
      success(`Đã lưu ${changedItems.size} học sinh!`);
      window.dispatchEvent(new Event('refreshBadge'));
    } catch (err) { error("Lỗi: " + err.message); } finally { setSaving(false); }
  };

  const handleSaveAndLeave = async () => { await handleSave(); confirmLeave(); };

  if (loading) return <div className="flex items-center justify-center h-64"><span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span></div>;

  return (
    <div className="space-y-4 pb-20 md:pb-6">
      <UnsavedModal show={showConfirm} onStay={confirmStay} onLeave={confirmLeave} onSaveAndLeave={handleSaveAndLeave} />

      <div className="flex items-center gap-2 flex-wrap">
        {isAdmin && (
          <select value={selectedLop} onChange={e => setSelectedLop(e.target.value)}
            className="bg-surface border border-outline-variant rounded-xl px-3 py-2.5 text-body-md font-semibold text-primary outline-none">
            <option value="">-- Chọn lớp --</option>
            {lopHocList.map(l => <option key={l.id} value={l.maLop}>{l.tenLop}</option>)}
          </select>
        )}
        <div className="flex items-center gap-1.5 bg-secondary-container rounded-xl px-3 py-2 text-caption font-semibold text-on-secondary-container whitespace-nowrap">
          <span className="material-symbols-outlined text-[16px]">school</span>{namHoc} • {hocKy}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline">search</span>
          <input type="text" placeholder="Tìm học sinh..." value={filterText} onChange={e => setFilterText(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-surface border border-outline-variant rounded-xl text-body-md focus:ring-2 focus:ring-secondary outline-none" />
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="bg-surface border border-outline-variant rounded-xl px-3 py-2.5 text-body-md outline-none">
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {canEdit && (
          <button onClick={handleSave} disabled={saving}
            className={`px-5 py-2.5 rounded-full font-body-lg flex items-center gap-2 shadow-md active:scale-95 transition-all ${saving ? 'bg-gray-400 text-white' : 'bg-primary text-on-primary hover:bg-primary/90'}`}>
            <span className="material-symbols-outlined text-[20px]">{saving ? 'sync' : 'save'}</span>
            <span className="hidden sm:inline">{saving ? 'Đang lưu...' : `Lưu (${changedItems.size})`}</span>
          </button>
        )}
      </div>

      <div className="md:hidden">
        <NhapDiemMobile selectedLop={selectedLop} hocSinh={hocSinh} diemData={diemData} changedItems={changedItems}
          activeTab={activeTab} setActiveTab={setActiveTab} canEdit={canEdit} isAdmin={isAdmin}
          getDiemTB={getDiemTB} updateDiem={updateDiem}
          setHasChanges={setHasChanges} setChangedItems={setChangedItems} setDiemData={setDiemData} LIMIT={LIMIT} />
      </div>

      <div className="hidden md:block">
        <NhapDiemDesktop selectedLop={selectedLop} hocSinh={hocSinh} diemData={diemData} changedItems={changedItems}
          canEdit={canEdit} isAdmin={isAdmin} getDiemTB={getDiemTB} updateDiem={updateDiem}
          setHasChanges={setHasChanges} setChangedItems={setChangedItems} setDiemData={setDiemData} LIMIT={LIMIT} />
      </div>
    </div>
  );
};

export default NhapDiem;